import type { ObstacleSlot } from './ChunkTypes'
import type { TrackChunkDefinition } from './ChunkTypes'
import type { Vec3 } from '../contracts/game'
import type { QuaternionData } from '../collision/ColliderTypes'
import { positionOnTrack, sampleChunkFrame } from './TrackFrame'

export interface ObstacleTransform {
  lateral: number
  vertical: number
  rotationZ: number
}

export interface ObstaclePlacement {
  readonly position: Vec3
  readonly rotation: QuaternionData
  readonly rotationZ: number
}

function quaternionFromEuler(pitch: number, yaw: number, roll: number): QuaternionData {
  const cy = Math.cos(yaw * 0.5)
  const sy = Math.sin(yaw * 0.5)
  const cp = Math.cos(pitch * 0.5)
  const sp = Math.sin(pitch * 0.5)
  const cr = Math.cos(roll * 0.5)
  const sr = Math.sin(roll * 0.5)
  return {
    x: sr * cp * cy - cr * sp * sy,
    y: cr * sp * cy + sr * cp * sy,
    z: cr * cp * sy - sr * sp * cy,
    w: cr * cp * cy + sr * sp * sy,
  }
}

export function sampleObstacleTransform(
  slot: Readonly<ObstacleSlot>,
  tick: number,
  out: ObstacleTransform,
): ObstacleTransform {
  const time = tick / 120
  out.lateral = slot.x
  out.vertical = 0
  out.rotationZ = 0
  if (slot.behavior === 'slide') out.lateral += Math.sin(time * 1.8) * 5
  else if (slot.behavior === 'rotate') out.rotationZ = time * 2.4
  else if (slot.behavior === 'crusher') out.vertical = Math.max(0, Math.sin(time * 1.35)) * 3
  return out
}

export function sampleObstaclePlacement(
  chunk: Readonly<TrackChunkDefinition>,
  slot: Readonly<ObstacleSlot>,
  tick: number,
): ObstaclePlacement {
  const transform = sampleObstacleTransform(slot, tick, { lateral: 0, vertical: 0, rotationZ: 0 })
  const frame = sampleChunkFrame(chunk, slot.z)
  const surface = positionOnTrack(chunk, slot.z, transform.lateral)
  const vertical = slot.height * 0.5 + transform.vertical
  const yaw = Math.atan2(frame.forward.x, frame.forward.z)
  const pitch = Math.atan2(frame.forward.y, Math.hypot(frame.forward.x, frame.forward.z))
  return {
    position: {
      x: surface.x + frame.up.x * vertical,
      y: surface.y + frame.up.y * vertical,
      z: surface.z + frame.up.z * vertical,
    },
    rotation: quaternionFromEuler(pitch, yaw, chunk.bank + transform.rotationZ),
    rotationZ: chunk.bank + transform.rotationZ,
  }
}
